import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation, useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useReajusteListaPagination } from "../hooks/indice_reajuste_cadastro/pagination";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { RateioGeralLista } from "../../domain/models/rateio_geral_listas";
import { RateioGeralContaPagar } from "../../domain/models/rateio_geral_conta_pagar";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { useGridApiRef } from "@mui/x-data-grid-pro";

type LoadReajusteCadastroState = {
    idContaPagar?: number;

    page?: number;
    rowsPerPage?: number;

}

export const useRateioGeralCadastroViewModel = () => {

    const {
        financeiro: {
            getAllRateioGeralUseCase,
            getContaPagarRateioByIdUseCase,
            updateRateioGeralUseCase,
            createRateioGeralUseCase
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getCentroCustoDropDownUseCase,
        },

    } = useDependencyInjection();


    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const debouncedRef = useCreateDebounceHandler(1500);

    const location = useLocation();
    const navigate = useNavigate();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();

    const [selectedIdContasPagar, setSelectedIdContasPagar] = useState<number>();
    const [rateioGeralLista, setRateioGeralLista] = useState<RateioGeralLista[]>([]);
    const [rateioGeralContaPagar, setRateioGeralContaPagar] = useState<RateioGeralContaPagar | null>();
    const [voltarContaPagar, setVoltarContaPagar] = useState<boolean>(false);

    const [openCadastroCentroCustoRateioModal, setOpenCadastroCentroCustoRateioModal] = useState<boolean>(false);
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [selectedIdsCentroCusto, setSelectedIdsCentroCusto] = useState<number[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const pagination = useReajusteListaPagination();

    const apiRef = useGridApiRef();

    //#region CARREGAMENTO.


    const loadAllRateioGeral = useCallback(async (funcState?: LoadReajusteCadastroState, idRateioGeralParam?: number) => {

        try {
            const idContaPagar = evaluateParamValue(funcState?.idContaPagar, selectedIdContasPagar);

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllRateioGeralUseCase.getAllRateioGeralLista(
                idContaPagar ?? 0,
                filteredPage,
                filteredRowsPerPage
            );

            setRateioGeralLista(getAllRateioGeralUseCase.items);
            pagination.setTotalItems(getAllRateioGeralUseCase.totalItems);
        } catch (e: unknown) {
            setRateioGeralLista([]);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar rateios gerais!'));
        }
    }, [
        selectedIdContasPagar,
        pagination,
        getAllRateioGeralUseCase,
        pushErrorNotification,
    ]);


    const loadContaPagarRateio = useCallback(async (idContaPagar: number) => {
        try {

            setPageLoading(true);
            const contaPagarRateioData = await getContaPagarRateioByIdUseCase.getContaPagarRateioById(idContaPagar)
            setRateioGeralContaPagar(contaPagarRateioData);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar rateio geral!'));
        } finally {
            setPageLoading(false);
        }
    }, [getContaPagarRateioByIdUseCase, pushErrorNotification]);


    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase]);
    //#endregion





    //#region PAGE START

    usePageStartBootstrap(async () => {
        try {

            const idContaPagarRateio = selectedIdContasPagar ?? location.state?.idContasPagar as number
            const voltarContaPagar = location.state?.conta as boolean
            setVoltarContaPagar(voltarContaPagar ?? false);

            if (idContaPagarRateio !== undefined) {

                setSelectedIdContasPagar(idContaPagarRateio);
                await loadCentroCustoDropDown();
                await loadAllRateioGeral({ idContaPagar: idContaPagarRateio });
                await loadContaPagarRateio(idContaPagarRateio);

            } else {
                setErrorMessage("Parâmetro id de conta pagar é obrigatório!");
            }

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar rateios gerais!")[0]);
        }

    });

    //#endregion



    //region CADASTRO CENTRO CUSTO RATEIO
    const handleIdCentroCustoChange = useCallback((newValues: number[]) => {
        setSelectedIdsCentroCusto(newValues);

    }, []);

    const handleSubmitCentroCustoRateio = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdsCentroCusto.length === 0) {
                pushWarnNotification('Atenção', 'Nenhum rateio para salvar!');
                return;
            }
            if (selectedIdContasPagar === undefined) {
                pushWarnNotification('Atenção', 'Selecione uma conta pagar para o rateio!');
                return;
            }

            await createRateioGeralUseCase.createRateioGeral(selectedIdContasPagar, selectedIdsCentroCusto);
            pushInfoNotification('Success', 'centro de custos salvos com sucesso!');

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao salvar centro de custos!'));
        } finally {
            setSaving(false);
            setOpenCadastroCentroCustoRateioModal(false);
            await loadAllRateioGeral({ idContaPagar: selectedIdContasPagar });
        }

    }, [
        selectedIdsCentroCusto,
        selectedIdContasPagar,
        createRateioGeralUseCase,
        pushInfoNotification,
        pushWarnNotification,
        pushErrorNotification]);

    //#endregion



    //#region EDIÇÃO RATEIOS


    const handleValorChange = (novoValor: string | null, idRateioGeral: number) => {
        setRateioGeralLista(prevState => {
            const itemAtual = prevState.find(
                item => item.idRateioGeral === idRateioGeral
            );

            if (!itemAtual) {
                return prevState;
            }

            const itemAtualizado: RateioGeralLista = {
                ...itemAtual,
                valorRateio: novoValor ? Number(novoValor) : null,
            };

            // Atualiza imediatamente o estado interno do DataGrid
            if (apiRef.current) {
                apiRef.current.updateRows([
                    itemAtualizado
                ]);
            }

            return prevState.map(item =>
                item.idRateioGeral === idRateioGeral
                    ? itemAtualizado
                    : item
            );
        });

        /*
        setRateioGeralLista((prevState) => {
            return prevState.map((item): RateioGeralLista => {
                if (item.idRateioGeral === idRateioGeral) {
                    return { ...item, valorRateio: novoValor !== null ? Number(novoValor) : null };
                }
                return item;
            });
        });
        */
    };

    const handleSubmitValoresRateios = useCallback(async () => {
        try {

            setSaving(true);
            if (rateioGeralLista.length === 0) {
                pushWarnNotification('Atenção', 'Nenhum rateio para salvar!');
                return;
            }

            await updateRateioGeralUseCase.updateRateioGeral(rateioGeralLista);
            pushInfoNotification('Success', 'Valores de rateios salvos com sucesso!');

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao salvar valores de rateios!'));
        } finally {
            setSaving(false);
        }

    }, [
        rateioGeralLista,
        updateRateioGeralUseCase,
        pushErrorNotification]);


    //#endregion


    //#region PAGINAÇÃO.

    const handlePageBack = useCallback(() => {
        if (voltarContaPagar) {
            navigate('/ContasPagar_cadastro', { state: { idContasPagar: selectedIdContasPagar } });
        } else {
            navigate('/RateioGeral_lista');
        }
    }, [
        voltarContaPagar,
        navigate]);


    const handleReload = useCallback(async () => {
        try {
            if (selectedIdContasPagar)
                setLoading(true);
            await loadAllRateioGeral({ page: pagination.page, rowsPerPage: pagination.rowsPerPage }, selectedIdContasPagar);
            await loadContaPagarRateio(selectedIdContasPagar ?? 0);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de reajustes!'));
        } finally {
            setLoading(false);
        }
    }, [selectedIdContasPagar]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadAllRateioGeral({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdContasPagar, loadAllRateioGeral]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadAllRateioGeral({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar reajustes!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdContasPagar, loadAllRateioGeral]);

    //#endregion

    //#region HISTORICO
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedIdContasPagar ?? 0, LogTipoTabela.CONTAS_PAGAR, true);
            }
        }
    }, [
        selectedIdContasPagar,
        changeHistory,
    ]);


    //#endregion


    return {
        loading,
        saving,
        errorMessage,
        pagination,
        rateioGeralLista: rateioGeralLista ?? [],
        selectedIdContasPagar,
        changeHistory,
        rateioGeralContaPagar,
        ddlCentroCusto,
        selectedIdsCentroCusto,
        openCadastroCentroCustoRateioModal,
        apiRef,

        handleSubmitCentroCustoRateio,
        handleValorChange,
        setOpenCadastroCentroCustoRateioModal,
        handleOpenChangeHistory,
        handleIdCentroCustoChange,
        handleSubmitValoresRateios,
        handlePageBack,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,

    };

}


